import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { X, Plus, Loader2 } from 'lucide-react';
import { useApiMutation } from '../../hooks/useApi';
import { contractorAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AddContractorForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    license_status: '',
    specialties: [],
    other_specialty: '',
    membership_level: 'Basic',
    status: 'Active',
    source: 'Application'
  });
  const [currentSpecialty, setCurrentSpecialty] = useState('');

  const { mutate, loading, error } = useApiMutation();
  const { toast } = useToast();

  const availableSpecialties = [
    'Plumbing', 'Electrical', 'HVAC', 'Carpentry', 'Painting', 
    'Flooring', 'Roofing', 'Landscaping', 'Cleaning', 'General Maintenance',
    'Appliance Repair', 'Pest Control', 'Locksmith', 'Drywall', 'Masonry',
    'Windows & Doors', 'Siding', 'Gutters', 'Fencing', 'Pool Maintenance'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addSpecialty = (specialty) => {
    if (specialty && !formData.specialties.includes(specialty)) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialty]
      }));
      setCurrentSpecialty('');
    }
  };

  const removeSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const contractorData = {
        ...formData,
        rating: 0.0,
        available: formData.status === 'Active'
      };

      const createdContractor = await mutate(contractorAPI.create, contractorData);

      toast({
        title: "Success!",
        description: `Contractor "${createdContractor.name}" has been added successfully.`,
      });

      onSuccess?.();
      onClose();

    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to add contractor. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Add New Contractor</CardTitle>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., John Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  required
                />
              </div>
              <div>
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Smith Plumbing LLC"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="license_status">License Status</Label>
                <Input
                  id="license_status"
                  name="license_status"
                  value={formData.license_status}
                  onChange={handleInputChange}
                  placeholder="e.g., Licensed, Unlicensed, Pending"
                />
              </div>
              <div>
                <Label htmlFor="membership_level">Membership Level *</Label>
                <select
                  id="membership_level"
                  name="membership_level"
                  value={formData.membership_level}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Basic">Basic</option>
                  <option value="Standard">Standard</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              <div>
                <Label htmlFor="source">Source *</Label>
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Application">Application</option>
                  <option value="Member">Member</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Specialty Skills *</Label>
              <div className="mt-2 space-y-3">
                <div className="flex gap-2">
                  <select
                    value={currentSpecialty}
                    onChange={(e) => setCurrentSpecialty(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a specialty</option>
                    {availableSpecialties
                      .filter(s => !formData.specialties.includes(s))
                      .map(specialty => (
                        <option key={specialty} value={specialty}>
                          {specialty}
                        </option>
                      ))}
                  </select>
                  <Button
                    type="button"
                    onClick={() => addSpecialty(currentSpecialty)}
                    disabled={!currentSpecialty}
                    className="px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {formData.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.specialties.map(specialty => (
                      <Badge 
                        key={specialty} 
                        variant="secondary" 
                        className="flex items-center gap-1"
                      >
                        {specialty}
                        <button
                          type="button"
                          onClick={() => removeSpecialty(specialty)}
                          className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {formData.specialties.length === 0 && (
                  <p className="text-sm text-gray-500">Please add at least one specialty</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="other_specialty">Other Specialty (Optional)</Label>
              <Input
                id="other_specialty"
                name="other_specialty"
                value={formData.other_specialty}
                onChange={handleInputChange}
                placeholder="e.g., Custom Woodwork, Historical Restoration"
              />
              <p className="text-xs text-gray-500 mt-1">Any additional specialties not listed above</p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || formData.specialties.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding Contractor...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contractor
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddContractorForm;